import { CSSObject } from "@emotion/react";
import { LoadingShimmer } from "@newsela/angelou";

import { TripleBarData } from "@/components/DataInfoCard/types";
import getCommaSeparatedNumericString from "@/utils/getCommaSeparatedNumericString";
import getPercentValue from "@/utils/getPercentValue";
import {
  barCssForDouble,
  barCss,
  barOneCss,
  barThreeCss,
  barTwoCss,
  barValueContainerOneCss,
  barValueContainerThreeCss,
  barValueContainerTwoCss,
  labelCss,
  labelOneCss,
  labelThreeCss,
  labelTwoCss,
  loadingContainer,
  loadingShimmerCss,
  valueCss,
  widgetTripleBarContainerCss,
  widgetDoubleBarsContainerCss,
} from "./TripleHorizontalBars.style";

interface TripleHorizontalBarProps {
  tripleBarData:
    | {
        barOne: TripleBarData;
        barTwo: TripleBarData;
        barThree?: TripleBarData | undefined;
        doubleBars?: boolean | undefined;
      }
    | undefined;
  additionalStyles?:
    | {
        containerCss?: CSSObject | undefined;
        statValueNumberCss?: CSSObject | undefined;
        loadingBarOneBgColorCss?: string | undefined;
        loadingBarOneShimmerColorCss?: string | undefined;
        loadingBarTwoBgColorCss?: string | undefined;
        loadingBarThreeBgColorCss?: string | undefined;
      }
    | undefined;
  loading?: boolean | undefined;
}

const TripleHorizontalBars = ({
  tripleBarData,
  additionalStyles,
  loading,
}: TripleHorizontalBarProps) => {
  const barOneValue = tripleBarData?.barOne.value ?? 0;
  const barTwoValue = tripleBarData?.barTwo.value ?? 0;

  const barThreeValue = !tripleBarData?.doubleBars
    ? (tripleBarData?.barThree?.value ?? 0)
    : 0;

  const highestValue = Math.max(barOneValue, barTwoValue, barThreeValue);

  /**
   * taking 80% of height to account for spacing for bar and value
   */
  const calculateBarHeight = (barValue: number) => {
    return barValue === 0
      ? barValue
      : getPercentValue(barValue, highestValue) * 0.8;
  };

  const heightOfBarOne = calculateBarHeight(barOneValue);
  const heightOfBarTwo = calculateBarHeight(barTwoValue);
  const heightOfBarThree = calculateBarHeight(barThreeValue);

  const loadingTwoBars = (
    <div css={loadingContainer} data-testid="loading-triple-bars">
      {/* row 1 */}
      <div css={[labelCss, labelOneCss]}>{tripleBarData?.barOne.label}</div>
      <div css={barValueContainerOneCss}>
        <div
          css={[
            barCss,
            barOneCss(heightOfBarOne),
            tripleBarData?.barOne?.style,
          ]}
        >
          <LoadingShimmer
            backgroundColor={additionalStyles?.loadingBarOneBgColorCss || ""}
            shimmerColor={additionalStyles?.loadingBarOneShimmerColorCss || ""}
            __cssFor={{
              shimmer: loadingShimmerCss,
            }}
          />
        </div>
      </div>
      {/* row 2 */}
      <div css={[labelCss, labelTwoCss]}>{tripleBarData?.barTwo.label}</div>
      <div css={barValueContainerTwoCss}>
        <div
          css={[
            barCss,
            barTwoCss(heightOfBarTwo),
            tripleBarData?.barTwo?.style,
          ]}
        >
          <LoadingShimmer
            backgroundColor={additionalStyles?.loadingBarTwoBgColorCss || ""}
            shimmerColor={additionalStyles?.loadingBarOneShimmerColorCss || ""}
            __cssFor={{
              shimmer: loadingShimmerCss,
            }}
          />
        </div>
      </div>
    </div>
  );

  const loadingThreeBars = (
    <div css={loadingContainer} data-testid="loading-triple-bars">
      {/* row 1 */}
      <div css={[labelCss, labelOneCss]}>{tripleBarData?.barOne.label}</div>
      <div css={barValueContainerOneCss}>
        <div
          css={[
            barCss,
            barOneCss(heightOfBarOne),
            tripleBarData?.barOne?.style,
          ]}
        >
          <LoadingShimmer
            backgroundColor={additionalStyles?.loadingBarOneBgColorCss || ""}
            shimmerColor={additionalStyles?.loadingBarOneShimmerColorCss || ""}
            __cssFor={{
              shimmer: loadingShimmerCss,
            }}
          />
        </div>
      </div>
      {/* row 2 */}
      <div css={[labelCss, labelTwoCss]}>{tripleBarData?.barTwo.label}</div>
      <div css={barValueContainerTwoCss}>
        <div
          css={[
            barCss,
            barTwoCss(heightOfBarTwo),
            tripleBarData?.barTwo?.style,
          ]}
        >
          <LoadingShimmer
            backgroundColor={additionalStyles?.loadingBarTwoBgColorCss || ""}
            shimmerColor={additionalStyles?.loadingBarOneShimmerColorCss || ""}
            __cssFor={{
              shimmer: loadingShimmerCss,
            }}
          />
        </div>
      </div>
      {/* row 3 */}
      <div css={[labelCss, labelThreeCss]}>
        {tripleBarData?.barThree?.label}
      </div>
      <div css={barValueContainerThreeCss}>
        <div
          css={[
            barCss,
            barThreeCss(heightOfBarThree),
            tripleBarData?.barThree?.style,
          ]}
        >
          <LoadingShimmer
            backgroundColor={additionalStyles?.loadingBarThreeBgColorCss || ""}
            shimmerColor={additionalStyles?.loadingBarOneShimmerColorCss || ""}
            __cssFor={{
              shimmer: loadingShimmerCss,
            }}
          />
        </div>
      </div>
    </div>
  );

  const twoBars = (
    <>
      {/* row 1 */}
      <div css={[labelCss, labelOneCss]}>{tripleBarData?.barOne.label}</div>
      <div css={barValueContainerOneCss}>
        <div
          css={[
            barCssForDouble,
            barOneCss(heightOfBarOne),
            tripleBarData?.barOne?.style,
          ]}
        />
        <div css={[valueCss, additionalStyles?.statValueNumberCss]}>
          {getCommaSeparatedNumericString(barOneValue)}
        </div>
      </div>
      {/* row 2 */}
      <div css={[labelCss, labelTwoCss]}>{tripleBarData?.barTwo.label}</div>
      <div css={barValueContainerTwoCss}>
        <div
          css={[
            barCssForDouble,
            barTwoCss(heightOfBarTwo),
            tripleBarData?.barTwo?.style,
          ]}
        />
        <div css={[valueCss, additionalStyles?.statValueNumberCss]}>
          {getCommaSeparatedNumericString(barTwoValue)}
        </div>
      </div>
    </>
  );
  const threeBars = (
    <>
      {/* row 1 */}
      <div css={[labelCss, labelOneCss]}>{tripleBarData?.barOne.label}</div>
      <div css={barValueContainerOneCss}>
        <div
          css={[
            barCss,
            barOneCss(heightOfBarOne),
            tripleBarData?.barOne?.style,
          ]}
        />
        <div css={[valueCss, additionalStyles?.statValueNumberCss]}>
          {getCommaSeparatedNumericString(barOneValue)}
        </div>
      </div>
      {/* row 2 */}
      <div css={[labelCss, labelTwoCss]}>{tripleBarData?.barTwo.label}</div>
      <div css={barValueContainerTwoCss}>
        <div
          css={[
            barCss,
            barTwoCss(heightOfBarTwo),
            tripleBarData?.barTwo?.style,
          ]}
        />
        <div css={[valueCss, additionalStyles?.statValueNumberCss]}>
          {getCommaSeparatedNumericString(barTwoValue)}
        </div>
      </div>

      {/* row 3 */}
      <div css={[labelCss, labelThreeCss]}>
        {tripleBarData?.barThree?.label}
      </div>
      <div css={barValueContainerThreeCss}>
        <div
          css={[
            barCss,
            barThreeCss(heightOfBarThree),
            tripleBarData?.barThree?.style,
          ]}
        />
        <div css={[valueCss, additionalStyles?.statValueNumberCss]}>
          {getCommaSeparatedNumericString(barThreeValue)}
        </div>
      </div>
    </>
  );

  const renderBars = () => {
    if (tripleBarData?.doubleBars) {
      return (
        <div
          css={[widgetDoubleBarsContainerCss, additionalStyles?.containerCss]}
        >
          {loading ? loadingTwoBars : twoBars}
        </div>
      );
    } else {
      return (
        <div
          css={[widgetTripleBarContainerCss, additionalStyles?.containerCss]}
        >
          {loading ? loadingThreeBars : threeBars}
        </div>
      );
    }
  };

  return renderBars();
};

export default TripleHorizontalBars;
